import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, ASSET_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth, getUser, type UserRole } from '../utils/auth';
import type { ApiResponse, VideoDescriptor } from '../types/api';
import { getVideoCoverUrl, handleCoverError } from '../utils/videoCover';
import AppShell from './AppShell';
import { Select, SelectItem } from './Select';
import { Input } from './Input';
import { Lock, Globe } from 'lucide-react';
import './VideoEdit.css';

const VideoEdit: React.FC = () => {
  const [video, setVideo] = useState<VideoDescriptor | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // 编辑状态
  const [editingName, setEditingName] = useState<string>('');
  const [editingVisibility, setEditingVisibility] = useState<'private' | 'public'>('private');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 从URL参数或location state中获取videoId
    const params = new URLSearchParams(location.search);
    const videoId = params.get('videoId');

    if (videoId) {
      loadVideo(videoId);
    } else {
      navigate('/profile');
    }
  }, [location, navigate]);

  const loadVideo = async (videoId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/video/${videoId}`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('获取视频信息失败');
      }

      const data: ApiResponse<VideoDescriptor> = await response.json();
      if (data.success && data.data) {
        setVideo(data.data);
        setEditingName(data.data.name || data.data.videoId);
        setEditingVisibility(data.data.visibility || 'private');
      } else {
        window.alert(data.error || '获取视频信息失败');
        navigate('/video-management');
      }
    } catch (err) {
      console.error('加载视频失败:', err);
      window.alert('网络错误，请稍后重试');
      navigate('/video-management');
    } finally {
      setLoading(false);
    }
  };
 
  // Cover upload
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [localCoverPreview, setLocalCoverPreview] = useState<string | null>(null);

  const handleCoverClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCoverSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !video) return;

    // Preview immediately
    const previewUrl = URL.createObjectURL(file);
    setLocalCoverPreview(previewUrl);
    setUploadingCover(true);

    try {
      const form = new FormData();
      form.append('videoId', video.videoId);
      form.append('cover', file);

      const headers = { ...(getAuthHeaders() as Record<string, string>) };
      // Remove Content-Type so the browser will set multipart/form-data with boundary
      delete headers['Content-Type'];

      const resp = await fetch(`${API_BASE_URL}/video/update-cover`, {
        method: 'POST',
        headers,
        body: form,
      });

      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const result: ApiResponse<{ coverUrl?: string }> = await resp.json();
      if (result.success) {
        // refresh video data if backend provides coverUrl or re-load
        if (result.data?.coverUrl) {
          setVideo(prev => prev ? { ...prev, coverUrl: result.data!.coverUrl } : prev);
        } else {
          // reload full video descriptor
          await loadVideo(video.videoId);
        }
        setSuccessMessage('封面已更新');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setLocalCoverPreview(null);
        window.alert(result.error || '封面上传失败');
      }
    } catch (err) {
      console.error('封面上传失败', err);
      setLocalCoverPreview(null);
      window.alert('上传失败，请稍后重试');
    } finally {
      setUploadingCover(false);
      // clear file input value so selecting same file again will trigger change
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!video) return;

    if (!editingName.trim()) {
      window.alert('视频名称不能为空');
      return;
    }

    setUpdating(true);

    try {
      // 先更新名称
      const nameResponse = await fetch(`${API_BASE_URL}/video/update-name`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: video.videoId,
          name: editingName.trim()
        }),
      });

      if (nameResponse.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const nameData: ApiResponse = await nameResponse.json();
      if (!nameData.success) {
        window.alert(nameData.error || '更新名称失败');
        return;
      }

      // 再更新可见性
      const visibilityResponse = await fetch(`${API_BASE_URL}/video/update-visibility`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: video.videoId,
          visibility: editingVisibility
        }),
      });

      const visibilityData: ApiResponse = await visibilityResponse.json();
      if (!visibilityData.success) {
        window.alert(visibilityData.error || '更新可见性失败');
        return;
      }

      // 更新成功，显示成功消息
      setSuccessMessage('保存成功');
      // 3秒后自动清除成功消息
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      window.alert('网络错误，请稍后重试');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="video-edit-container">
        <div className="loading-message">加载中...</div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="video-edit-container">
        <div className="error-message">视频不存在</div>
      </div>
    );
  }

  const header = (
    <div className="video-edit-header app-shell-header-content">
      <button
        onClick={handleCancel}
        className="back-button"
        title="取消并返回"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>编辑视频</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="video-edit-container">
        <div className="video-edit-content">
        {/* 视频封面 */}
        <div className="video-cover-section">
          <div className="video-cover-large">
            <img
              src={localCoverPreview || getVideoCoverUrl(video)}
              alt={video.name || video.videoId}
              onError={handleCoverError}
              onClick={handleCoverClick}
              role="button"
              tabIndex={0}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCoverSelected}
            />
            <div className="cover-edit-hint" onClick={handleCoverClick}>
              {uploadingCover ? '上传中...' : '更换封面'}
            </div>
            {video.capabilities?.hasWordAlignment && (
              <div className="video-badge-large">
                <span className="icon-inline" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2 5 5 .5-4 3 1 5-4-2-4 2 1-5-4-3 5-.5z"></path></svg></span>
                <span>词级对齐</span>
              </div>
            )}
          </div>
        </div>

        {/* 编辑表单 */}
        <div className="edit-form">
          <div className="form-group">
            <label className="form-label">视频ID</label>
            <div className="video-id-display">{video.videoId}</div>
          </div>

          <div className="form-group">
            <label className="form-label">视频名称</label>
            <Input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              placeholder="请输入视频名称"
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label className="form-label">可见性</label>
            <Select
              value={editingVisibility}
              onValueChange={(value) => setEditingVisibility(value as 'private' | 'public')}
              disabled={updating}
            >
              <SelectItem value="private"><Lock size={14} className="icon-inline" /> 私有</SelectItem>
              <SelectItem value="public"><Globe size={14} className="icon-inline" /> 公开</SelectItem>
            </Select>
          </div>

          {successMessage && <div className="success-message">{successMessage}</div>}

          <div className="form-actions">
            <button
              onClick={handleSave}
              className="save-button"
              disabled={updating}
            >
              {updating ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
        </div>
      </div>
    </AppShell>
  );
};

export default VideoEdit;
